import { useState } from "react";

interface FormData {
  buyerName: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  productCategory: string;
  productDescription: string;
  quantity: string;
  containerType: string;
  targetPrice: string;
  deliveryPort: string;
  timeline: string;
  notes: string;
}

const emptyForm: FormData = {
  buyerName: "", email: "", phone: "", company: "", country: "",
  productCategory: "", productDescription: "", quantity: "",
  containerType: "", targetPrice: "", deliveryPort: "", timeline: "", notes: "",
};

const containerTypes = [
  "20ft Standard (25 CBM)",
  "40ft Standard (55 CBM)",
  "40ft High Cube (67 CBM)",
  "LCL (Less than Container Load)",
  "Multiple Containers",
];

const categories = [
  "Electronics", "Apparel & Textiles", "Home & Garden", "Health & Beauty",
  "Sports & Fitness", "Auto Parts", "Machinery & Equipment", "Food & Beverage",
  "Furniture", "Building Materials", "Toys & Games", "Other",
];

const countries = [
  "United States", "United Kingdom", "Canada", "Australia", "Germany",
  "France", "Netherlands", "UAE", "Singapore", "Japan", "South Korea",
  "Brazil", "Mexico", "India", "South Africa", "Nigeria", "Other",
];

const stats = [
  { icon: "ri-ship-line", value: "50,000+", label: "Containers Shipped" },
  { icon: "ri-building-2-line", value: "20M+", label: "Verified Factories" },
  { icon: "ri-global-line", value: "30+", label: "Countries Served" },
  { icon: "ri-time-line", value: "24hrs", label: "Quote Response" },
];

export default function BulkOrderSection() {
  const [form, setForm] = useState<FormData>(emptyForm);
  const [charCount, setCharCount] = useState(0);
  const [notesCount, setNotesCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field: keyof FormData, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.buyerName || !form.email || !form.productCategory || !form.productDescription || !form.quantity || !form.containerType) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    const body = new URLSearchParams({
      buyerName: form.buyerName,
      email: form.email,
      phone: form.phone,
      company: form.company,
      country: form.country,
      productCategory: form.productCategory,
      productDescription: form.productDescription,
      quantity: form.quantity,
      containerType: form.containerType,
      targetPrice: form.targetPrice,
      deliveryPort: form.deliveryPort,
      timeline: form.timeline,
      notes: form.notes,
    });
    try {
      await fetch("https://readdy.ai/api/form/d7cn8nnsch5bvrijf6m0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch (_e) { /* silent */ }
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

        {/* Section header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="w-8 h-px bg-amber-400 inline-block"></span>
            <p className="text-amber-500 text-sm font-bold uppercase tracking-widest">Bulk Trade</p>
            <span className="w-8 h-px bg-amber-400 inline-block"></span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
            Container Bulk Order Inquiry
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">
            Ready to order by the container? Submit your requirements and our trade specialists will match you with verified factories within 24 hours.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {stats.map(s => (
            <div key={s.label} className="bg-amber-50 rounded-2xl p-5 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl mx-auto mb-3">
                <i className={`${s.icon} text-amber-500 text-2xl`}></i>
              </div>
              <div className="text-2xl font-extrabold text-gray-900 mb-0.5">{s.value}</div>
              <div className="text-xs text-gray-500 font-semibold">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: info panel */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/54875a208494146dc3985b1b6079924f.jpg"
                alt="Container Shipping"
                className="w-full h-52 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white font-extrabold text-lg">Ship by the Container</p>
                <p className="text-white/70 text-xs">20ft · 40ft · 40ft HC · LCL available</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-5">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-question-answer-line text-amber-500"></i>
                How It Works
              </h3>
              <ul className="flex flex-col gap-4">
                {[
                  { step: "01", title: "Submit Inquiry", desc: "Fill in your product requirements, quantity, and target price." },
                  { step: "02", title: "Factory Matching", desc: "Our team matches you with 3–5 verified factories within 24 hours." },
                  { step: "03", title: "Get Quotes", desc: "Receive competitive FOB/CIF quotes directly from factories." },
                  { step: "04", title: "Place Order", desc: "Confirm order, arrange payment, and track your container shipment." },
                ].map(s => (
                  <li key={s.step} className="flex gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-amber-400 text-white text-xs font-extrabold rounded-full shrink-0">{s.step}</span>
                    <div>
                      <p className="font-bold text-gray-800 text-sm">{s.title}</p>
                      <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <h3 className="font-bold text-amber-700 mb-3 flex items-center gap-2">
                <i className="ri-shield-check-line text-amber-500"></i>
                Trade Assurance Included
              </h3>
              <ul className="flex flex-col gap-2">
                {[
                  "Payment held until shipment confirmed",
                  "Factory audit reports available",
                  "Pre-shipment quality inspection",
                  "Full refund if specs not met",
                  "Dedicated trade specialist assigned",
                ].map(b => (
                  <li key={b} className="flex items-center gap-2 text-xs text-amber-700">
                    <i className="ri-checkbox-circle-fill text-amber-400 shrink-0"></i>{b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white border border-gray-100 rounded-2xl p-12 flex flex-col items-center text-center h-full justify-center">
                <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-5">
                  <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Inquiry Submitted!</h3>
                <p className="text-gray-500 text-sm mb-2 max-w-sm">
                  Your bulk order inquiry has been received. Our trade specialists will contact you within <strong>24 hours</strong> with factory matches and quotes.
                </p>
                <p className="text-amber-500 text-sm font-semibold mb-6">Check your email: {form.email}</p>
                <div className="bg-amber-50 rounded-xl p-4 w-full max-w-sm mb-6 text-left">
                  <p className="text-xs font-bold text-amber-700 mb-2">Your Inquiry Summary</p>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Category</span>
                      <span className="font-semibold text-gray-700">{form.productCategory}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Container</span>
                      <span className="font-semibold text-gray-700">{form.containerType}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Quantity</span>
                      <span className="font-semibold text-gray-700">{form.quantity} units</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { setSubmitted(false); setForm(emptyForm); setCharCount(0); setNotesCount(0); }}
                  className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 flex flex-col gap-5"
              >
                <h3 className="font-extrabold text-gray-900 text-lg flex items-center gap-2">
                  <i className="ri-ship-line text-amber-500"></i>
                  Submit Your Bulk Order Requirements
                </h3>

                {/* Buyer info */}
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Your Information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name *</label>
                      <input
                        required
                        name="buyerName"
                        value={form.buyerName}
                        onChange={e => handleChange("buyerName", e.target.value)}
                        placeholder="John Smith"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Email *</label>
                      <input
                        required
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={e => handleChange("email", e.target.value)}
                        placeholder="john@company.com"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone / WhatsApp</label>
                      <input
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={e => handleChange("phone", e.target.value)}
                        placeholder="+1 555 000 0000"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Company Name</label>
                      <input
                        name="company"
                        value={form.company}
                        onChange={e => handleChange("company", e.target.value)}
                        placeholder="Your Company Ltd."
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Country / Destination</label>
                    <select
                      name="country"
                      value={form.country}
                      onChange={e => handleChange("country", e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                    >
                      <option value="">Select your country</option>
                      {countries.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Product requirements */}
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Product Requirements</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Category *</label>
                      <select
                        required
                        name="productCategory"
                        value={form.productCategory}
                        onChange={e => handleChange("productCategory", e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                      >
                        <option value="">Select category</option>
                        {categories.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Container Type *</label>
                      <select
                        required
                        name="containerType"
                        value={form.containerType}
                        onChange={e => handleChange("containerType", e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                      >
                        <option value="">Select container</option>
                        {containerTypes.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">
                      Product Description * <span className="text-gray-400 font-normal">({charCount}/500)</span>
                    </label>
                    <textarea
                      required
                      name="productDescription"
                      value={form.productDescription}
                      onChange={e => {
                        if (e.target.value.length <= 500) {
                          handleChange("productDescription", e.target.value);
                          setCharCount(e.target.value.length);
                        }
                      }}
                      placeholder="Describe the product you need: specifications, materials, dimensions, colors, certifications required..."
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                    />
                    {charCount >= 500 && <p className="text-red-500 text-xs mt-1">Maximum 500 characters reached.</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Quantity (units) *</label>
                      <input
                        required
                        name="quantity"
                        type="number"
                        min="1"
                        value={form.quantity}
                        onChange={e => handleChange("quantity", e.target.value)}
                        placeholder="e.g. 5000"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Target Price (USD/unit)</label>
                      <input
                        name="targetPrice"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.targetPrice}
                        onChange={e => handleChange("targetPrice", e.target.value)}
                        placeholder="e.g. 12.50"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Delivery Port</label>
                      <input
                        name="deliveryPort"
                        value={form.deliveryPort}
                        onChange={e => handleChange("deliveryPort", e.target.value)}
                        placeholder="e.g. Los Angeles"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Timeline & notes */}
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Additional Details</p>
                  <div className="mb-3">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Required Timeline</label>
                    <select
                      name="timeline"
                      value={form.timeline}
                      onChange={e => handleChange("timeline", e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                    >
                      <option value="">Select timeline</option>
                      <option>ASAP (within 2 weeks)</option>
                      <option>1 month</option>
                      <option>2–3 months</option>
                      <option>3–6 months</option>
                      <option>Flexible</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">
                      Additional Notes <span className="text-gray-400 font-normal">({notesCount}/500)</span>
                    </label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={e => {
                        if (e.target.value.length <= 500) {
                          handleChange("notes", e.target.value);
                          setNotesCount(e.target.value.length);
                        }
                      }}
                      placeholder="Any special requirements, certifications needed, packaging preferences, or other details..."
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                    />
                  </div>
                </div>

                {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-400 hover:bg-amber-500 disabled:bg-amber-300 text-white font-extrabold py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> Submitting...
                    </span>
                  ) : (
                    <span><i className="ri-ship-line mr-2"></i>Submit Bulk Order Inquiry — Free</span>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400">
                  No commitment required · Factory quotes within 24 hours · Trade Assurance included
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
